/**
 * Dashboard: the home screen. A campaign carousel with an All campaigns stop, the selected campaign's metrics,
 * sends chart and funnel, the inboxes, and the machinery.
 * Calls only the Engine API (VITE_ENGINE_API_URL). A value with no route reads not connected.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ViewGrid } from 'iconoir-react'
import { PageWrapper } from '@/components/layout/Shell'
import { useAuth } from '@/hooks/useAuth'
import { useSelectedWorkspace } from '@/hooks/useSelectedWorkspace'
import { createBusinessCampaignClient } from '@/lib/business-campaign-client'
import { createReplyEventsClient } from '@/lib/reply-events-client'
import { createEngineMailboxClient } from '@/lib/engine-mailbox-client'
import { buildOpsModel, loadOpsSources } from '@/lib/ops-matrix'
import OpsMatrixView from '@/components/engine/dashboard/OpsMatrixView'
import { createSendsClient } from '@/components/engine/dashboard/sends-source'

const build = make => { try { return make() } catch { return null } }

export default function Dashboard({ deadlineMs } = {}) {
  const { getToken, user, sessionId, isAuthenticated, role } = useAuth()
  const workspaceId = useSelectedWorkspace()
  const baseUrl = import.meta.env.VITE_ENGINE_API_URL
  // getToken may change identity on any render. The clients read the latest one, so a new identity never rebuilds them
  // and never restarts the load (a restart sets the page back to Loading, which renders again: a loop with no end).
  const tokenSource = useRef(getToken)
  tokenSource.current = getToken
  const stableToken = useCallback(() => tokenSource.current(), [])
  const clients = useMemo(() => {
    if (!isAuthenticated || !user?.id || !sessionId || !baseUrl) return { business: null, replies: null, mailboxes: null, sends: null }
    const options = { baseUrl, getToken: stableToken, allowLocal: import.meta.env.DEV, expectedUserId: user.id, expectedSessionId: sessionId, workspaceId }
    return {
      business: build(() => createBusinessCampaignClient(options)),
      replies: build(() => createReplyEventsClient(options)),
      mailboxes: build(() => createEngineMailboxClient(options)),
      sends: build(() => createSendsClient(options)),
    }
  }, [stableToken, user?.id, sessionId, isAuthenticated, baseUrl, workspaceId])

  // The answer is kept with the workspace it was read for, so no render for another selection carries it.
  const [slot, setSlot] = useState({ workspaceId: null, value: null })
  const setSources = value => setSlot({ workspaceId, value })
  const sources = slot.workspaceId === workspaceId ? slot.value : null
  const latest = useRef(0)

  const load = useCallback(async signal => {
    const mine = ++latest.current
    try {
      const next = await loadOpsSources({ ...clients, signal, deadlineMs })
      if (mine === latest.current) setSources(next)
    } catch (e) {
      if (e?.name !== 'AbortError' && mine === latest.current) setSources({ campaigns: { state: 'unavailable' }, held: { state: 'unavailable' }, boxes: { state: 'unavailable' }, series: { state: 'unavailable' } })
    }
  }, [clients, deadlineMs])

  useEffect(() => {
    const controller = new AbortController()
    setSlot(s => (s.value === null && s.workspaceId === workspaceId ? s : { workspaceId, value: null }))
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  const model = useMemo(() => (sources ? buildOpsModel(sources) : null), [sources])

  return (
    <PageWrapper title="Dashboard" icon={ViewGrid}>
      <OpsMatrixView scope={workspaceId} state={model ? 'ready' : 'loading'} model={model} spendVisible={role === 'admin' || role === 'operator'} />
    </PageWrapper>
  )
}
